
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import BaseCodedWidget from './BaseCodedWidget.vue';
import dbw from '@/worker/dbw';
import { FilterableCardName } from '@/store/models.def';
import moment from 'moment';
import {
    getTableName,
} from '@/worker/db/sqlHelpers';

@Component({
    components: {},
})
export default class FilterWidget extends BaseCodedWidget {
    @Prop({default: () => []}) public selectedAgents!: string[];
    @Prop({default: () => []}) public selectedCustomers!: string[];
    @Prop({default: () => []}) public selectedStockItems!: string[];
    @Prop({default: () => []}) public selectedSuppliers!: string[];
    @Prop({default: () => []}) public selectedProject!: string;
    @Prop({default: () => []}) public selectedProjects!: string[];
    @Prop({default: () => []}) public selectedDocProjects!: string[];
    @Prop({default: () => []}) public selectedDTLProjects!: string[];
    @Prop({default: () => []}) public selectedAreas!: string[];
    @Prop({
        default: moment().startOf('day').valueOf(),
    })
    public selectedAsOfDate!: number;
    @Prop({
        default: () => [
            moment().startOf('day').add(-1, 'year').valueOf(),
            moment().endOf('day').valueOf(),
        ],
    })
    public selectedDateRange!: [number, number];
    @Prop({default: null}) public currentFocusingFilterId!: string | null;

    /**
     * Ensure all required tables are loaded into DuckDB before running queries.
     * Returns a queryHead that can be used for rawQuery calls.
     * Use this when querying multiple tables with noFilters() pattern.
     *
     * Note: Retry logic is necessary because Vue reactivity triggers widget
     * re-renders immediately when account changes, but table loading is async.
     * The retry handles the gap between accountId changing and tables being ready.
     *
     * @param tables - Array of table names to ensure are loaded
     * @returns A queryHead that can be used for rawQuery calls
     */
    /**
     * Ensure all required tables are loaded before executing cross-table queries.
     * Each getHead() triggers table registration in DuckDB.
     * Returns the first head for executing rawQuery.
     */
    protected async _ensureTablesLoaded(
        tables: FilterableCardName[],
    ): Promise<any> {
        const heads = await Promise.all(
            tables.map((table) => dbw.loadTable(table)),
        );
        return heads[0];
    }

    /**
     * Stock movement = SUM(amount at previous month) - SUM(amount at current month).
     * Requires 'stockBalances' in _ensureTablesLoaded.
     */
    protected async _getStockMovement(
        queryHead: any,
        dateRange: [number, number],
        project?: string | string[],
    ): Promise<number> {
        const sbTable = getTableName('stockBalances');
        const d0 = moment(dateRange[0]).add(-1, 'month');
        const prevMonth = d0.month() + 1;
        const prevYear = d0.year();
        const d1 = moment(dateRange[1]);
        const curMonth = d1.month() + 1;
        const curYear = d1.year();

        let projectFilter = '';
        if (project) {
            const projects = typeof project === 'string' ? [project] : project;
            const escaped = projects.map(
                (p) => `'${p.replace(/'/g, '\'\'')}'`,
            ).join(',');
            projectFilter = `WHERE "PROJECT" IN (${escaped})`;
        }

        const result = await queryHead.rawQuery(`
            SELECT
                SUM(CASE WHEN TRY_CAST("SYEAR" AS INT) = ${prevYear}
                    AND TRY_CAST("SMONTH" AS INT) = ${prevMonth}
                    THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END)
                - SUM(CASE WHEN TRY_CAST("SYEAR" AS INT) = ${curYear}
                    AND TRY_CAST("SMONTH" AS INT) = ${curMonth}
                    THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END)
                as stockMovement
            FROM ${sbTable}
            ${projectFilter}
        `);
        return result[0] && Number(result[0].stockMovement) || 0;
    }

    /**
     * Stock movement 2 = (amount at current month + amount at previous month) / 2.
     * Uses first matching row per month (like Array.find, not reduce).
     * Requires 'stockBalances' in _ensureTablesLoaded.
     */
    protected async _getStockMovement2(
        queryHead: any,
        dateRange: [number, number],
    ): Promise<number> {
        const sbTable = getTableName('stockBalances');
        const d0 = moment(dateRange[0]).add(-1, 'month');
        const prevMonth = d0.month() + 1;
        const prevYear = d0.year();
        const d1 = moment(dateRange[1]);
        const curMonth = d1.month() + 1;
        const curYear = d1.year();

        const result = await queryHead.rawQuery(`
            SELECT TRY_CAST("SYEAR" AS INT) as yr,
                TRY_CAST("SMONTH" AS INT) as mo,
                TRY_CAST("AMOUNT" AS DOUBLE) as amt
            FROM ${sbTable}
            WHERE (TRY_CAST("SYEAR" AS INT) = ${prevYear}
                    AND TRY_CAST("SMONTH" AS INT) = ${prevMonth})
                OR (TRY_CAST("SYEAR" AS INT) = ${curYear}
                    AND TRY_CAST("SMONTH" AS INT) = ${curMonth})
        `);
        let amountBefore = 0;
        let amountAfter = 0;
        for (const row of result) {
            const yr = Number(row.yr);
            const mo = Number(row.mo);
            if (yr === prevYear && mo === prevMonth && amountBefore === 0) {
                amountBefore = Number(row.amt) || 0;
            }
            if (yr === curYear && mo === curMonth && amountAfter === 0) {
                amountAfter = Number(row.amt) || 0;
            }
        }
        return (amountAfter + amountBefore) / 2;
    }
}
